import React, { useState, useEffect, useRef } from 'react';
import styles from './InsertContentPopup.module.css';
import DOMPurify from 'dompurify';
import AlbumViewer from './AlbumViewer';
import RawHtmlEditor from './RawHtmlEditor';
import { t } from 'i18next';
import { useCCContext } from '../CC/CCContext';
import * as ccUtils from '../CC/utils';
import GenericIconButton from '../GenericComponents/GenericIconButton';

export interface ExtensionProps {
  state: string;
  query: string;
  htmlStyle: string;
  imageHeight?: string;
  numImages?: string;
  margin?: string;
  layout?: string;
  spacing?: string;
  padding?: string;
  rowHeight?: string;
  columns?: string;
  linkText?: string;
  linkAlbumId?: string;
  imageUrl?: string;
  imageAlt?: string;
  compClass?: string;
  html?: string;
}

interface InsertContentPopupProps {
  onInsert: (extension: string, props: ExtensionProps) => void;
  onCancel: () => void;
  initialExtension?: string;
  initialProps?: ExtensionProps;
}

const InsertContentPopup: React.FC<InsertContentPopupProps> = ({
  onInsert,
  onCancel,
  initialExtension,
  initialProps,
}) => {
  const [selectedExtension, setSelectedExtension] = useState<
    'Count' | 'Link' | 'AlbumLink' | 'AlbumEmbed' | 'MyImage' | 'ImageList' | 'RecordBrowser' | 'RecordBrowserPopup' | 'RawHtml'
  >(
    (initialExtension as any) || 'Count'
  );

  const [props, setProps] = useState<ExtensionProps>(
    initialProps || {
      state: '',
      query: '',
      htmlStyle: '',
      imageHeight: '100',
      numImages: '100',
      margin: '5',
      layout: 'columns',
      spacing: '0',
      padding: '0',
      rowHeight: '50',
      columns: '3',
      linkText: '',
      linkAlbumId: '',
      imageUrl: '',
      imageAlt: '',
      compClass: '',
      html: '',
    }
  );

  // -------------------------------
  // Added for resizable panes
  // -------------------------------
  const [leftPaneWidth, setLeftPaneWidth] = useState<number>(300); // Initial width in pixels
  const isResizingRef = useRef(false);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    isResizingRef.current = true;
    // Start listening at the document level
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizingRef.current) return;
    // For a horizontal resize, we can set the leftPane width based on mouse X
    // Optional: add boundary checks or min/max widths
    setLeftPaneWidth(e.clientX);
  };

  const handleMouseUp = () => {
    isResizingRef.current = false;
    // Stop listening once mouse is released
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };
  // -------------------------------
  const { cc } = useCCContext();
  const handleGetStateButtonClick = () => {
    const state = ccUtils.GenerateState(cc);
    setProps((prevProps) => ({ ...prevProps, state: state }));
  }

  useEffect(() => {
    if (selectedExtension === 'Count') {
      // Example resetting some fields:
      setProps((prevProps) => ({
        ...prevProps,
        imageHeight: '100',
        linkText: '',
        linkAlbumId: '',
        imageUrl: '',
        imageAlt: '',
        numImages: '100',
        margin: '5',
        html: '',
        compClass: ''
      }));
    }
  }, [selectedExtension]);

  const handlePropChange = (prop: keyof ExtensionProps, value: string) => {
    let sanitizedValue = value;

    // Example sanitizing
    if (prop === 'htmlStyle') {
      sanitizedValue = DOMPurify.sanitize(value, { ALLOWED_ATTR: ['style'] });
    }
    if (prop === 'html') {
      sanitizedValue = DOMPurify.sanitize(value);
    }

    setProps((prevProps) => ({ ...prevProps, [prop]: sanitizedValue }));
  };

  const handlePropChangeRawHtml = (prop: keyof ExtensionProps, value: string) => {
    setProps((prevProps) => ({ ...prevProps, [prop]: value }));
  };

  const handleInsert = () => {
    onInsert(selectedExtension, props);
  };

  const renderExample = () => {
    if (selectedExtension === 'RawHtml') {
      // Render sanitized HTML
      return (
        <div
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(props.html || ''),
          }}
        />
      );
    } else {
      // For other extensions, show the custom element
      const attributes = Object.entries(props)
        .filter(([_, value]) => value != null && value !== '')
        .map(([key, value]) => {
          if (key === 'htmlStyle') {
            return `style="${value.replace(/"/g, '"')}"`;
          }
          const dataKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
          let escapedValue = value;
          if (typeof value === 'object') {
            escapedValue = JSON.stringify(value).replace(/"/g, '"');
          } else if (typeof value === 'string') {
            escapedValue = value.replace(/"/g, '"');
          }
          return `data-${dataKey}="${escapedValue}"`;
        })
        .join(' ');

      const albumSource = `<${selectedExtension.toLowerCase()} ${attributes}></${selectedExtension.toLowerCase()}>`;
      return <AlbumViewer albumSource={albumSource} />;
    }
  };

  return (
    <div className={styles.popup}>
      <div className={styles.content}>
        {/* Left Pane */}
        <div
          className={styles.leftPane}
          style={{ width: leftPaneWidth }}
        >
          <select
            value={selectedExtension}
            onChange={(e) =>
              setSelectedExtension(e.target.value as typeof selectedExtension)
            }
            className={styles.combobox}
          >
            <option value="Count">{t("insertcontentpopup_count")}</option>
            <option value="Link">{t("insertcontentpopup_link")}</option>
            <option value="AlbumLink">{t("insertcontentpopup_linkToAlbum")}</option>
            <option value="AlbumEmbed">{t("insertcontentpopup_embedAlbum")}</option>
            <option value="MyImage">{t("insertcontentpopup_image")}</option>
            <option value="ImageList">{t("insertcontentpopup_imageList")}</option>
            <option value="RecordBrowser">{t("insertcontentpopup_recordBrowser")}</option>
            <option value="RecordBrowserPopup">{t("insertcontentpopup_recordBrowserAsPopup")}</option>
            <option value="RawHtml">Html brut</option>
          </select>

          <div className={styles.properties}>
            {selectedExtension === 'RawHtml' ? (
              <RawHtmlEditor
                initialHtml={props.html || ''}
                onChange={(html: string) => handlePropChangeRawHtml('html', html)}
              />
            ) : (
              <>
                {/* Normal property fields for other extensions */}
                {(selectedExtension !== 'AlbumLink' && selectedExtension !== 'AlbumEmbed') && (
                    <label>
                    {t("insertcontentpopup_state")}
                    <input
                      type="text"
                      value={props.state}
                      onChange={(e) => handlePropChange('state', e.target.value)}
                      style={{width: '94%', marginRight: '5px'}}
                    /><GenericIconButton 
                    icon='regular/code.svg'
                    hoverEnabled={false}
                    width='16px' 
                    height='16px'
                    onClick={() => {handleGetStateButtonClick()}}
                />
                  </label>
                )}
                {(selectedExtension !== 'AlbumLink' && selectedExtension !== 'AlbumEmbed') && (
                  <label>
                {t("insertcontentpopup_htmlStyle")}
                <input
                      type="text"
                      value={props.htmlStyle}
                      onChange={(e) => handlePropChange('htmlStyle', e.target.value)}
                      placeholder="par ex. color: green; font-weight: bold;"
                    />
                  </label>
                )}
                {selectedExtension === 'ImageList' && (
                  <>
                    <label>
                      Disposition:
                      <select onChange={(e) => handlePropChange('layout', e.target.value)}>
                        <option value="rows">En rangees</option>
                        <option value="columns">En colonnes</option>
                        <option value="masonry">En maconnerie</option>
                      </select>
                    </label>
                    <label>
                      Colonnes:
                      <input
                        type="number"
                        value={props.columns}
                        onChange={(e) => handlePropChange('columns', e.target.value)}
                        min="1"
                        max="10"
                      />
                    </label>
                    <label>
                      Nombre d'images:
                      <input
                        type="number"
                        value={props.numImages}
                        onChange={(e) => handlePropChange('numImages', e.target.value)}
                        min="1"
                      />
                    </label>
                    <label>
                      Passepartout:
                      <input
                        type="number"
                        value={props.padding}
                        onChange={(e) => handlePropChange('padding', e.target.value)}
                        min="0"
                        max="50"
                      />
                    </label>
                    <label>
                      Espacement:
                      <input
                        type="number"
                        value={props.spacing}
                        onChange={(e) => handlePropChange('spacing', e.target.value)}
                        min="0"
                        max="50"
                      />
                    </label>
                    <label>
                      Hauteur rangee:
                      <input
                        type="number"
                        value={props.rowHeight}
                        onChange={(e) => handlePropChange('rowHeight', e.target.value)}
                        min="50"
                        max="500"
                      />
                    </label>
                  </>
                )}
                {(selectedExtension === 'Link' ||
                  selectedExtension === 'AlbumLink' ||
                  selectedExtension === 'RecordBrowserPopup') && (
                  <label>
                {t("insertcontentpopup_linkText")}
                <input
                      type="text"
                      value={props.linkText}
                      onChange={(e) => handlePropChange('linkText', e.target.value)}
                    />
                  </label>
                )}
                {(selectedExtension === 'AlbumLink' ||
                  selectedExtension === 'AlbumEmbed') && (
                  <label>
                {t("insertcontentpopup_albumId")}
                <input
                      type="text"
                      value={props.linkAlbumId}
                      onChange={(e) => handlePropChange('linkAlbumId', e.target.value)}
                    />
                  </label>
                )}
                {selectedExtension === 'MyImage' && (
                  <>
                  <label>
                {t("insertcontentpopup_imageUrl")}
                <input
                      type="text"
                      value={props.imageUrl}
                      onChange={(e) => handlePropChange('imageUrl', e.target.value)}
                    />
                  </label>
                  <label>
                  Texte alternatif
                  <input
                        type="text"
                        value={props.imageAlt}
                        onChange={(e) => handlePropChange('imageAlt', e.target.value)}
                      />
                    </label>
                    </>
                  )}
                <label>
                Class CSS
                <input
                      type="text"
                      value={props.compClass}
                      onChange={(e) => handlePropChange('compClass', e.target.value)}
                    />
                  </label>
              </>
            )}
          </div>
        </div>

        {/* The Resizer */}
        <div
          className={styles.resizer}
          onMouseDown={handleMouseDown}
        />

        {/* Right Pane */}
        <div className={styles.rightPane}>
          <h3>{t("insertcontentpopup_example")}</h3>
          <div className={styles.scrollableExample}>{renderExample()}</div>
        </div>
      </div>

      <div className={styles.footer}>
        <button onClick={handleInsert} className={styles.insertButton}>
        {t("insertcontentpopup_insert")}
        </button>
        <button onClick={onCancel} className={styles.cancelButton}>
        {t("insertcontentpopup_cancel")}
        </button>
      </div>
    </div>
  );
};

export default InsertContentPopup;